// @flow
import React, { useCallback, useMemo } from "react";
import Modal from "components/Modal";
import ItemViewWorkflow from "components/CommandActions/itemViews/itemView_workflow";
import ItemViewActivityList from "components/CommandActions/itemViews/itemView_activityList";
import Icons from "@hs/icons";
import { useModalPostMessageHandler } from "hooks";
import { createModal } from "react-modal-promise";
import { getLocalizedText } from "data/utils";
import CommandActionHandler from "components/CommandActions";
import { produce } from "immer";
import styles from "./WorkflowActionPanel.module.css";

type Props = {
    /** react-modal-promise: whether to show dialog */
    open: boolean,
    /** react-modal-promise: callback when user closes Dialog */
    close: () => void,
};

/**
 * renders a docked modal as the actionPanel
 */
const WorkflowActionPanel = ({ open, close, contextProps }: Props) => {
    useModalPostMessageHandler(close);
    const { itemUri, formatId, isDoc } = contextProps;
    const handleWorkflowClick = useCallback(
        (action: ItemActionConfig) =>
            CommandActionHandler(
                "link",
                produce({}, (draft) => {
                    draft = Object.assign(draft, contextProps, {
                        asModal: true,
                        docked: true,
                        title: action.title,
                        url: action.url,
                    });
                })
            ),
        [contextProps]
    );
    const view = useMemo(
        () => ({
            isDoc,
            showHistory: false,
            showNotes: true,
            showAddNote: false,
            showRetentionInfo: false,
            customFilter: "wf@",
        }),
        [isDoc]
    );
    return (
        <Modal
            docked
            scroll
            show={open !== false}
            onHide={close}
            dataTest="actionPanelWorkflow"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <Icons.Custom
                        className={styles.icon}
                        icon="fa-regular fa-square-check"
                    />
                    <span className={styles.title}>
                        {getLocalizedText("$actionCategory:wf.name")}
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ margin: 15 }}>
                <ItemViewWorkflow
                    itemUri={itemUri}
                    onClick={handleWorkflowClick}
                />
                <h4 className={styles.heading}>
                    <Icons.Custom
                        className={styles.icon}
                        icon="fa-regular fa-clock-rotate-left"
                    />
                    <span className={styles.title}>
                        {getLocalizedText("$actionCategory:history.name")}
                    </span>
                </h4>
                <ItemViewActivityList
                    itemUri={itemUri}
                    formatId={formatId}
                    view={view}
                />
            </Modal.Body>
        </Modal>
    );
};
WorkflowActionPanel.displayName = "WorkflowActionPanel";

const openWorkflowActionPanel = createModal(WorkflowActionPanel);
export { WorkflowActionPanel, openWorkflowActionPanel };
